"use client"

//TASK CARD THAT DISPLAYS TASKS WITH COMMIT FEATURE
import { useState, useEffect, useRef } from "react"
import { ChevronDown, Pencil, MessageSquare, CircleCheckBig, GitCommit, Clock, User, File } from "lucide-react"
import CommentBox from "./CommentBox"
import { useAuth } from "../Contexts/AuthContext"
import TokenChecker, { WS_BASE_URL } from "./TokenChecker"
import FilePreviewCard from "./FilePreviewCard"
import { log, error as logError } from "../utils/logger";

// Priority border color mapping
const priorityColors = {
  Immediate: "border-red-500",
  Moderate: "border-yellow-500",
  Lenient: "border-green-500",
}

//helper functions to format dates
const toISO = (s) => {
  // handle "Month D, YYYY" or ISO; fallback to today
  const tryDate = new Date(s)
  if (!isNaN(tryDate)) {
    const yyyy = tryDate.getFullYear()
    const mm = String(tryDate.getMonth() + 1).padStart(2, "0")
    const dd = String(tryDate.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }
  return s // if backend already provides ISO
}

const toPretty = (iso) => {
  const d = new Date(iso)
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
}

// Reusable Card component
const Card = ({ children, className = "" }) => (
  <div className={`bg-white shadow-lg rounded-md ${className}`}>{children}</div>
)

// Priority Dropdown component
const PriorityDropdown = ({ priority, onPriorityChange, editable = false }) => {
  const [isOpen, setIsOpen] = useState(false)

  const priorityColors = {
    Immediate: "border-red-500 text-red-500",
    Moderate: "border-yellow-500 text-yellow-500",
    Lenient: "border-green-500 text-green-500",
  }

  const handlePriorityChange = (newPriority) => {
    if (editable) {
      onPriorityChange(newPriority)
      setIsOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        disabled={!editable}
        className={`flex items-center justify-between w-[130px] px-3 py-1 text-sm border rounded-md ${priorityColors[priority]} ${
          !editable ? "cursor-not-allowed pl-8" : ""
        }`}
        onClick={() => editable && setIsOpen(!isOpen)}
      >
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
        {!editable ? "" : <ChevronDown className={`w-4 h-4 ml-2 ${!editable ? "text-gray-300" : ""}`} />}
      </button>

      {isOpen && editable && (
        <div className="absolute z-10 w-[130px] mt-1 bg-white border rounded-md shadow-lg">
          {Object.keys(priorityColors).map((p) => (
            <button
              key={p}
              className={`block w-full text-left px-3 py-2 text-sm ${
                p === priority ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
              onClick={() => handlePriorityChange(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Reusable Button component
const Button = ({ variant = "default", className = "", children, ...props }) => {
  const baseStyles =
    "px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4299E1]"
  const variantStyles = {
    default: "bg-[#4299E1] text-white hover:bg-[#3182CE]",
    outline: "border border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]",
    ghost: "text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF]",
  }

  return (
    <button className={`${baseStyles} ${variantStyles[variant]} ${className}`} {...props}>
      {children}
    </button>
  )
}

// Commit Message Modal Component
const CommitModal = ({ isOpen, onClose, onSubmit, progress, previousProgress, onRevert, title, priority }) => {
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    await onSubmit(message.trim())
    setMessage("")
    setIsSubmitting(false)
    // DO NOT call onClose() here; parent closes after save
  }

  const handleSkip = async () => {
    setIsSubmitting(true)
    await onSubmit("")
    setMessage("")
    setIsSubmitting(false)
  }

  const handleClose = () => {
    if (onRevert) {
      onRevert() // Revert to previous progress
    }
    onClose() // Close the modal
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-96 max-w-90vw border-t-8 ${priorityColors[priority]}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitCommit className="w-5 h-5 text-[#4299E1]" />
            <h3 className="text-lg font-semibold">Progress Update</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-lg leading-none"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 bg-[#EDF6FB] rounded-md">
          <h4 className="text-md font-bold text-[#2D3748] mb-1">{title}</h4>
          <p className="text-sm text-[#4A5568]">
            Progress:{" "}
            <span className="font-semibold">
              {previousProgress}% → {progress}%
            </span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#4A5568] mb-2">What did you work on? (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a brief message about your progress..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1] resize-none"
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{message.length}/200 characters</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={handleSkip} disabled={isSubmitting}>
              Skip
            </Button>
            <Button type="submit" disabled={isSubmitting} className="min-w-20">
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Progress History Component
const ProgressHistory = ({ taskId, isOpen, onClose, title, priority }) => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const { authData } = useAuth()

  useEffect(() => {
    if (isOpen && taskId) {
      fetchProgressLogs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taskId])

  const fetchProgressLogs = async () => {
    setLoading(true)
    try {
      const response = await TokenChecker(`/progress-logs/${taskId}/`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      const data = await response.json()

      // Remove duplicates based on timestamp and author
      const uniqueLogs = []
      const seen = new Set()

      const sortedLogs = (data.logs || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

      sortedLogs.forEach((log) => {
        const key = `${log.author_name}-${log.created_at}-${log.progress}`
        if (!seen.has(key)) {
          seen.add(key)
          uniqueLogs.push(log)
        }
      })

      setLogs(uniqueLogs)
    } catch (error) {
      logError("Error fetching progress logs:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (isoString) => {
    const date = new Date(isoString)
    return date.toLocaleDateString() + " at " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  // Generate consistent colors for different users
  const getUserColor = (authorName) => {
    const colors = [
      "border-green-500",
      "border-purple-500",
      "border-orange-500",
      "border-pink-500",
      "border-indigo-500",
      "border-yellow-500",
    ]

    const currentUserName = `${authData.firstName || ""} ${authData.lastName || ""}`.trim()

    if (authorName === currentUserName) {
      return "border-[#4299E1]" // Always blue for current user
    }

    // Generate consistent color based on name hash
    let hash = 0
    for (let i = 0; i < authorName.length; i++) {
      hash = authorName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  const getDisplayName = (authorName) => {
    const currentUserName = `${authData.firstName || ""} ${authData.lastName || ""}`.trim()
    return authorName === currentUserName ? "You" : authorName
  }

  if (!isOpen) return null

  // Split logs into latest and past
  const latestLog = logs.length > 0 ? logs[0] : null
  const pastLogs = logs.slice(1)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-lg p-6 w-[600px]  border-t-8 ${priorityColors[priority]}  max-w-90vw max-h-80vh overflow-hidden`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitCommit className="w-5 h-5 text-[#4299E1]" />
            <h3 className="text-lg font-semibold">Progress History</h3>
            <h4 className=" block text-md text-gray-800 font-medium"> - {title}</h4>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading history...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No progress updates yet</div>
          ) : (
            <div className="space-y-4">
              {/* Latest Log Section */}
              {latestLog && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Latest Update</h4>
                  <div
                    className={`border-l-4 ${getUserColor(latestLog.author_name)} pl-4 pb-3 bg-gray-50 rounded-r-md pr-3 pt-2`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex items-center gap-1 text-sm font-medium text-[#4A5568]">
                        <User className="w-4 h-4" />
                        {getDisplayName(latestLog.author_name)}
                      </div>
                      <span className="text-sm font-semibold text-[#4299E1]">{latestLog.progress}%</span>
                    </div>

                    {latestLog.message && <p className="text-sm text-[#4A5568] mb-2 italic">"{latestLog.message}"</p>}

                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(latestLog.created_at)}
                    </div>
                  </div>
                </div>
              )}

              {/* Past Logs Section */}
              {pastLogs.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Past Updates</h4>
                  <div className="space-y-3">
                    {pastLogs.map((log, index) => (
                      <div key={log.id} className={`border-l-4 ${getUserColor(log.author_name)} pl-4 pb-3`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex items-center gap-1 text-sm font-medium text-[#4A5568]">
                            <User className="w-4 h-4" />
                            {getDisplayName(log.author_name)}
                          </div>
                          <span className="text-sm font-semibold text-[#4299E1]">{log.progress}%</span>
                        </div>

                        {log.message && <p className="text-sm text-[#4A5568] mb-2 italic">"{log.message}"</p>}

                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Main TaskCard component
const TaskCard = ({
  id,
  title,
  description,
  dueDate,
  priority: initialPriority,
  progress: initialProgress,
  owner,
  isCompleted = false,
  markComplete,
  onDelete,
  category,
  allowEdit,
  allowDelete,
  allowComplete,
  allowProgress,
  handleEdit,
  viewusers,
  invitedUsers,
  assignedUsers,
  isTrusted,
  isAccepted,
  canComment,
  onDueDateChange,
  attachmentKey = null, // New prop for attachment key
}) => {
  const [priority, setPriority] = useState(initialPriority)
  const [progress, setProgress] = useState(initialProgress)
  const [editDueDate, setEditDueDate] = useState(toISO(dueDate))
  useEffect(() => {
    setEditDueDate(toISO(dueDate))
  }, [dueDate])

  const [editMode, setEditmode] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showCommentBox, setShowCommentBox] = useState(false)
  const [newCommentAlert, setNewCommentAlert] = useState(false)

  // Commit feature states
  const [showCommitModal, setShowCommitModal] = useState(false)
  const [pendingProgress, setPendingProgress] = useState(null)
  const [showProgressHistory, setShowProgressHistory] = useState(false)
  const [lastCommitMessage, setLastCommitMessage] = useState("")
  const [showPreview, setShowPreview] = useState(false)

  const socketRef = useRef(null)
  const { authData } = useAuth()

  const originalProgressRef = useRef(progress)
  // Debounce + session tracking for progress changes
  const changeStartRef = useRef(null)
  const promptDebounceRef = useRef(null)
  const showModalRef = useRef(false)

  useEffect(() => {
    socketRef.current = new WebSocket(`${WS_BASE_URL}/ws/comments/${id}/`)

    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data)
      if (data.comment && !showCommentBox) {
        setNewCommentAlert(true)
      }
    }

    return () => {
      socketRef.current?.close()
    }
  }, [id, showCommentBox])

  useEffect(() => {
    setProgress(initialProgress)
  }, [initialProgress])

  useEffect(() => {
    if (isCompleted) {
      setProgress(100)
    }
  }, [isCompleted])

  // Keep a ref mirror of modal state for timeout callback freshness
  useEffect(() => {
    showModalRef.current = showCommitModal
  }, [showCommitModal])

  const saveProgressWithCommit = async (progressValue, commitMessage = "") => {
    try {
      const response = await TokenChecker(`/progress/${id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          progress: progressValue,
          message: commitMessage,
        }),
      })

      if (response.ok) {
        // eslint-disable-next-line no-unused-vars
        const data = await response.json()
        setLastCommitMessage(commitMessage)
        log("Progress updated with commit")
      }
    } catch (error) {
      logError("Error saving progress:", error)
    }
  }

  // Debounced prompt: open modal 15ms after last change if value changed
  const queuePromptCheck = (finalValue) => {
    if (!allowProgress || isCompleted || !isAccepted) return
    if (changeStartRef.current === null) {
      changeStartRef.current = originalProgressRef.current
    }
    if (promptDebounceRef.current) clearTimeout(promptDebounceRef.current)
    promptDebounceRef.current = setTimeout(() => {
      const start = changeStartRef.current
      changeStartRef.current = null
      if (showModalRef.current) return
      if (start === null) return
      if (finalValue !== start) {
        setPendingProgress({ new: finalValue, previous: start, original: originalProgressRef.current })
        setShowCommitModal(true)
      }
    }, 550)
  }

  const handleProgressInput = (e) => {
    const newProgress = Math.round(e.target.value / 5) * 5
    setProgress(newProgress)
    queuePromptCheck(newProgress)
  }

  const handleProgressChange = (e) => {
    const newProgress = Math.round(e.target.value / 5) * 5
    setProgress(newProgress)
    queuePromptCheck(newProgress)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (promptDebounceRef.current) clearTimeout(promptDebounceRef.current)
    }
  }, [])

  const handleCommitSubmit = async (commitMessage) => {
    if (pendingProgress) {
      await saveProgressWithCommit(pendingProgress.new, commitMessage)
      // Update refs after successful commit
      originalProgressRef.current = pendingProgress.new
      setPendingProgress(null)
      setShowCommitModal(false)
    }
  }

  const handleRevert = () => {
    if (pendingProgress && pendingProgress.original !== undefined) {
      setProgress(pendingProgress.original)
      originalProgressRef.current = pendingProgress.original
    }
    setPendingProgress(null)
    setShowCommitModal(false)
  }

  const getProgressLabel = (progress) => {
    if (progress === 0) return "Not started"
    if (progress <= 30) return "Started"
    if (progress <= 60) return "Half way"
    if (progress < 100) return "Almost done"
    return "Done"
  }

  const bgPColor = {
    Immediate: "bg-red-500",
    Moderate: "bg-yellow-500",
    Lenient: "bg-green-500",
  }

  const pct = Number(progress) || 0

  return (
    <>
      <Card className={`mb-4 border-l-4 ${priorityColors[priority]} ${!isAccepted ? "opacity-50" : ""}`}>
        <div className="p-4">
          {/* Task Title and Priority */}
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-[#2D3748]">{title}</h3>
            <div className="flex items-center space-x-2">
              {/* Progress History Button */}
              {allowProgress && (
                <Button
                  variant="ghost"
                  className="flex items-center text-[#4A5568] hover:text-[#4299E1] space-x-1"
                  onClick={() => setShowProgressHistory(true)}
                  title="View progress history"
                >
                  <GitCommit className="w-4 h-4" />
                  <span className="text-sm">History</span>
                </Button>
              )}

              {/* Comment Button */}
              {canComment && (
                <Button
                  variant="ghost"
                  className="relative flex items-center text-[#4A5568] hover:text-[#4299E1] space-x-1"
                  onClick={() => {
                    setShowCommentBox(true)
                    setNewCommentAlert(false)
                  }}
                >
                  <MessageSquare className="w-5 h-6" />
                  {newCommentAlert && (
                    <span className="absolute top-0 right-0 h-3 w-3 rounded-full bg-blue-500 border-2 border-white" />
                  )}
                </Button>
              )}

              {/* Edit Button */}
              {!isCompleted && allowEdit && (
                <Button
                  variant="ghost"
                  className="flex items-center text-[#4A5568] hover:text-[#4299E1] space-x-1"
                  onClick={handleEdit}
                >
                  <Pencil className="w-4 h-4" />
                  <span className="text-sm">Edit</span>
                </Button>
              )}

              <PriorityDropdown priority={priority} onPriorityChange={setPriority} editable={editMode} />

              <div className="relative ml-2">
                <button
                  type="button"
                  className="text-sm text-[#4A5568] hover:text-[#4299E1]"
                  onClick={() => setShowUserDropdown((prev) => !prev)}
                  disabled={!viewusers || !isAccepted}
                >
                  <span className="ml-2 text-md text-[#4A5568] flex items-center gap-1">
                    {owner}
                    {isTrusted && <CircleCheckBig className="text-[#4299E1] w-5 h-5" title="Trusted User" />}
                  </span>
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white border-t-4 border-[#4299E1] rounded shadow-md z-10 text-sm text-[#4A5568] p-2">
                    {assignedUsers.length > 0 && (
                      <div className="mb-2">
                        <p className="font-semibold">Assigned:</p>
                        {assignedUsers.map((email, i) => (
                          <p key={i}>{email}</p>
                        ))}
                      </div>
                    )}
                    {invitedUsers.length > 0 && (
                      <div>
                        <p className="font-semibold">Invited:</p>
                        {invitedUsers.map((email, i) => (
                          <p key={i}>{email}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Task Description */}
          <div className="w-full mb-4">
            <div className="bg-[#EDF6FB] text-md text-[#4A5568] p-3 rounded-md leading-relaxed text-left whitespace-pre-wrap break-words">
              {isExpanded || description.length <= 150 ? description : `${description.slice(0, 150)}...`}
              {description.length > 150 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="ml-2 text-sm text-[#4299E1] underline focus:outline-none"
                >
                  {isExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className={`mb-4 ${isCompleted ? "opacity-50" : ""}`}>
            <div className="flex justify-between items-center mb-2">
              <div className="text-md mb-2 font-semibold text-[#4A5568]">
                {getProgressLabel(pct)}
                <p className="text-gray-400 text-md font-light inline">- {pct}%</p>
                {lastCommitMessage && (
                  <p className="text-xs text-gray-500 italic mt-1" title={lastCommitMessage}>
                    "{lastCommitMessage.length > 50 ? lastCommitMessage.slice(0, 50) + "..." : lastCommitMessage}"
                  </p>
                )}
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden h-1 text-xs flex rounded bg-[#EBF8FF]">
                {[...Array(21)].map((_, i) => {
                  const isDark = i % 2 === 0
                  const isFifty = i === 10
                  const barHeight = isFifty ? "h-6" : isDark ? "h-4" : "h-3"
                  const barColor = isFifty ? "bg-[#2D3748]" : isDark ? "bg-[#4A5568]" : "bg-[#A0AEC0]"

                  return (
                    <div
                      key={i}
                      className={`absolute -top-1 ${barHeight} w-[1px] ${barColor}`}
                      style={{ left: `${i * 5}%` }}
                    />
                  )
                })}

                {/* FILL — add transition */}
                <div
                  style={{ width: `${pct}%` }}
                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${bgPColor[priority]} transition-[width] duration-300 ease-in-out`}
                />

                {/* HANDLE — position from raw pct + transition */}
                <div
                  className={`absolute -top-3/4 w-4 h-3 bg-white shadow-xl border border-gray-300 rounded-full ${
                    !allowProgress || isCompleted ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } transition-[left] duration-300 ease-in-out`}
                  style={{ left: `calc(${pct}% - 8px)` }}
                />
              </div>

              {/* Let the range drive updates; debounce prompts by 15ms */}
              <input
                type="range"
                min="0"
                max="100"
                value={pct}
                onInput={handleProgressInput}
                onChange={handleProgressChange}
                disabled={isCompleted || !allowProgress || !isAccepted}
                className="absolute top-0 left-0 w-full h-2 opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {/* Due Date and Action Buttons */}
          <div className="flex justify-between items-center mt-6">
            {/* Due Date + File Link */}
            <div className="flex items-center space-x-3 text-[#2D3748] text-md">
              {editMode && onDueDateChange ? (
                <input
                  type="date"
                  className="text-md text-[#718096] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1]"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  required
                  onBlur={() => {
                    setEditmode(false)
                    onDueDateChange(editDueDate)
                  }}
                />
              ) : (
                <span className="flex items-center space-x-2">
                  <span className="text-md font-medium">{toPretty(dueDate)}</span>
                  {category && (
                    <Pencil
                      onClick={() => setEditmode(true)}
                      className="h-4 w-4 text-gray-500 hover:text-[#4299E1] cursor-pointer"
                    />
                  )}
                </span>
              )}

              {attachmentKey && (
                <button
                  onClick={() => setShowPreview(true)}
                  title="Preview Attachment"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md text-gray-500 hover:text-[#4299E1] hover:bg-[#D0EFFF] transition-colors"
                >
                  <File className="w-6 h-6" />
                </button>
              )}
            </div>
            {showPreview && (
              <FilePreviewCard
                taskId={id}
                title={title}
                dueDate={dueDate}
                priority={priority}
                assignedUsers={assignedUsers}
                invitedUsers={invitedUsers}
                onClose={() => setShowPreview(false)}
              />
            )}

            <div className="flex space-x-2">
              {allowComplete && (
                <Button
                  variant="outline"
                  className="text-[#4299E1] border-[#4299E1] hover:bg-[#EBF8FF] bg-transparent"
                  onClick={() => markComplete(id, isCompleted)}
                >
                  {isCompleted ? "Uncheck" : "Complete"}
                </Button>
              )}

              {allowDelete && (
                <Button
                  variant="outline"
                  className="text-red-500 border-red-500 hover:bg-red-50 bg-transparent"
                  onClick={onDelete}
                >
                  Delete task
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Comment Box */}
      {showCommentBox && (
        <div className="px-4 pb-4">
          <CommentBox
            taskId={id}
            userEmail={authData.email}
            title={title}
            priority={priority}
            onClose={() => setShowCommentBox(false)}
          />
        </div>
      )}

      <CommitModal
        isOpen={showCommitModal}
        onClose={() => {
          setShowCommitModal(false)
          setPendingProgress(null)
        }}
        onSubmit={handleCommitSubmit}
        progress={pendingProgress?.new || progress}
        previousProgress={pendingProgress?.previous || progress}
        onRevert={handleRevert}
        title={title}
        priority={priority}
      />

      {/* Progress History Modal */}
      <ProgressHistory
        taskId={id}
        title={title}
        priority={priority}
        isOpen={showProgressHistory}
        onClose={() => setShowProgressHistory(false)}
      />
    </>
  )
}

export default TaskCard
